import { Component, DestroyRef, inject, input, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-countdown-timer',
  imports: [NgClass],
  templateUrl: './countdown-timer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './countdown-timer.component.scss'
})
export class CountdownTimerComponent {
  // Inputs (in milliseconds)
  public readonly totalMilliseconds = input.required<number>();
  public readonly warnThresholdPercent = input<number>(40); // Default: warn at 40% remaining
  public readonly dangerThresholdPercent = input<number>(20); // Default: danger at 20% remaining
  public readonly label = input<string>('Tid tilbage:');
  public readonly expiredLabel = input<string>('Tid udløbet!');

  // State (in seconds for display)
  protected readonly secondsRemaining = signal<number>(0);
  protected readonly animate = signal<boolean>(true);
  
  // Computed
  protected readonly formattedTime = computed(() => this.formatTime(this.secondsRemaining()));
  
  // Calculate percent remaining
  protected readonly percentRemaining = computed(() => {
    const totalSeconds = Math.floor(this.totalMilliseconds() / 1000);
    return totalSeconds > 0 ? (this.secondsRemaining() / totalSeconds) * 100 : 0;
  });
  
  protected readonly progressPercent = computed(() => this.percentRemaining());

  private interval: any;
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy(() => this.stop());
  }

  // Public methods
  public start(): void {
    this.stop(); // Clear any existing interval
    
    // Disable animation for instant reset
    this.animate.set(false);
    
    const totalSeconds = Math.floor(this.totalMilliseconds() / 1000);
    this.secondsRemaining.set(totalSeconds);
    
    // Re-enable animation after reset
    setTimeout(() => this.animate.set(true), 50);
    
    this.interval = setInterval(() => {
      this.secondsRemaining.update(v => Math.max(v - 1, 0));
    }, 1000);
  }

  public stop(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  public reset(newTotalMs?: number): void {
    this.stop();
    
    // Disable animation for instant reset
    this.animate.set(false);
    
    const totalMs = newTotalMs ?? this.totalMilliseconds();
    const totalSeconds = Math.floor(totalMs / 1000);
    this.secondsRemaining.set(totalSeconds);
    
    // Re-enable animation
    setTimeout(() => this.animate.set(true), 50);
  }

  private formatTime(totalSeconds: number): string {
    if (totalSeconds <= 0) return '00:00';
    
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(1, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
}

